import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'

import { clsx } from 'clsx'
import { IconSearch, IconSpin, IconXmarkLight } from 'packages/component-library/dist'

import { SelectProductMobile } from './SelectProduct/mobile'
import { SEARCH_TAB_TYPE } from '~/types/search'
import { SelectItem } from '~/types/ui.components'

export type SearchProductsMobileProps = {
  onSearch: (query: string, type: SEARCH_TAB_TYPE, productId?: string) => void;
  allProductsLabel: string;
  initialText?: string;
  accentColor?: string;
  onChange?: (text: string) => void;
  isLoading?: boolean;
  onInputLengthChange?: (length: number) => void;
  products?: Array<{ id: string; name: string }>;
  currentProduct?: { id: string; name: string };
  tab: SEARCH_TAB_TYPE
}

export const SearchProductsMobile = ({
  initialText = '',
  accentColor = '',
  onSearch,
  onChange,
  isLoading = false,
  onInputLengthChange,
  products = [],
  currentProduct,
  allProductsLabel,
  tab,
}: SearchProductsMobileProps) => {
  const [text, setText] = useState<string>(initialText)
  const [prevInputValue, setPrevInputValue] = useState('')

  const selectOptions = [{ id: allProductsLabel, name: allProductsLabel }, ...products]

  const [selectedProduct, setSelectedProduct] = useState<SelectItem>(currentProduct || selectOptions[0])
  const textRef = useRef(text)
  
  useEffect(() => {
    onChange?.(text)
    textRef.current = text 
  }, [text])

  useEffect(() => {
    if (selectedProduct.id === allProductsLabel) {
      onSearch(text, tab)
    } else {
      onSearch(text, tab, selectedProduct.id)
    }
  }, [tab])
  
  const handleInputChange = useCallback((event: FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget
    setText(value)
    onInputLengthChange?.(value?.trim().length)

    if (value && value !== prevInputValue) {
      if (selectedProduct.id === allProductsLabel) {
        onSearch(value, tab)
      } else {
        onSearch(value, tab, selectedProduct.id)
      }
      setPrevInputValue(value)
    }
    
  }, [onInputLengthChange, selectedProduct])

  const onClear = useCallback(() => {
    setText('')
    onInputLengthChange?.(0)
  }, [onInputLengthChange])
  
  const [isHover, setIsHover] = useState(false)
  
  const onMouseEnter = () => {
    setIsHover(true)
  }
  
  const onMouseLeave = () => {
    setIsHover(false)
  }
  
  const [isFocused, setIsFocused] = useState(false)
  
  const onFocus = () => {
    setIsFocused(true)
  }
  
  const onBlur = () => {
    setIsFocused(false)
  }
  
  const onChangeProduct = (product: SelectItem) => {
    setSelectedProduct(product)
    if (product.id === allProductsLabel) {
      onSearch(textRef.current, tab)
    } else {
      onSearch(textRef.current, tab, product.id)
    }
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      <div
        className={clsx(
          'relative w-full rounded-md',
          'flex items-center rounded border border-neutral-40 w-full text-neutral-98',
          'dark:text-neutral-10 dark:bg-neutral-98 dark:border-neutral-70',
          !accentColor && 'hover:border-accent-60 focus:border-accent-60 dark:hover:border-accent-40 dark:focus:border-accent-40',
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid="search"
        style={(isHover || isFocused) ? { borderColor: accentColor } : {}}

      >
        <div className="flex items-center ml-3 mr-2 text-neutral-70 dark:text-neutral-40">
          <IconSearch width={16} height={16} />
        </div>
        <input
          aria-label="search input"
          type="text"
          value={text}
          onInput={handleInputChange}
          className="flex-grow block w-full py-2 border-0 text-neutral-98 dark:text-neutral-05 focus:ring-0 text-body2 dark:bg-transparent"
        />
        {isLoading ? (
          <div
            className="flex items-center pl-2 pr-3"
            data-testid="search-loading"
          >
            <IconSpin width={16} height={16} />
          </div>
        ) : (
          <div className="flex items-center ml-2 mr-3 cursor-pointer text-neutral-70 dark:text-neutral-60 hover:text-neutral-98">
            <div aria-label="search clean" onClick={onClear} className="flex items-center justify-center w-4 h-4 ">
              <IconXmarkLight />
            </div>
          </div>
        )}
      </div>
      <SelectProductMobile
        options={selectOptions}
        accentColor={accentColor}
        defaultValue={currentProduct ? currentProduct : selectOptions[0]}
        onChange={onChangeProduct}
      />
    </div>
  )
}